import "./dashboard.page.css";
import "../portal.css";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { Skeleton } from "antd";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { Navbar } from "@src/components/navbar";
import { DutyRequestFormModal } from "@src/components/dutyRequestForm/dutyRequestFormModal";
import { DutyDrawer } from "@src/components/dutyDrawer/dutyDrawer";
import { getAllSitesForCustomerQuery } from "@src/util/query/site.query";
import { getCustomerDutyRequestsQuery } from "@src/util/query/dutyRequest.query";
import { getCustomerIncidentAlertsQuery } from "@src/util/query/incident.query";
import { getCustomerPortalOverviewQuery } from "@src/util/query/dashboard.query";
import { getCustomerPortalTodayQuery } from "@src/util/query/portal.query";
import { getStatusMeta } from "@src/page/incidentPage/incidentMeta";
import { formatHours, officerName, stateMeta, timeRange } from "@src/util/portal/dutyState";

dayjs.extend(relativeTime);

// Dashboard - the customer's home: what needs attention right now, who is on duty
// (with proof one tap away), what's still to come and what's done, then the
// latest duty requests / incidents. Same look as the incident pages (white
// cards on #f5f8fa, app teals, orange CTA).

const REQUEST_STATUS = {
    pending:  { label: "Pending",  color: "#e8590c" },
    pushed:   { label: "Approved", color: "#16a34a" },
    rejected: { label: "Rejected", color: "#dc2626" },
};

// tile/filter key -> the duty states it covers
const GROUPS = [
    { key: "attention", label: "Needs attention", sub: "Missed clock-in, pending or no officer", states: ["missedClockIn", "noShow", "unassigned"], empty: "Nothing needs your attention." },
    { key: "onDuty",    label: "On duty now",     sub: "Clocked in at your sites", states: ["onDuty"], empty: "Nobody is on duty right now." },
    // later today, or due to start in the next few minutes - officer assigned, not clocked in yet
    { key: "upcoming",  label: "Still to come",   sub: "Later today, officer assigned", states: ["upcoming", "awaiting"], empty: "No more duties today." },
    { key: "completed", label: "Completed",       sub: "Finished today", states: ["completed"], empty: "No duties completed yet today." },
];
const GROUP_ORDER = { attention: 0, onDuty: 1, upcoming: 2, completed: 3 };
const groupOf = (state) => GROUPS.find((g) => g.states.includes(state))?.key || "upcoming";

const getGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
};

const getInitials = (name) => {
    if (!name) return "?";
    const words = name.trim().split(/\s+/);
    if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
    return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
};

const Pill = ({ label, color, bg }) => (
    <span className="cdb-pill" style={{ color, borderColor: color, background: bg || "#fff" }}>{label}</span>
);

const Card = ({ title, action, children }) => (
    <section className="cdb-card">
        <div className="cdb-card-head">
            <h3>{title}</h3>
            {action}
        </div>
        <div className="cdb-card-body">{children}</div>
    </section>
);

// one line under a duty: what has actually happened so far
const proofLine = (row) => {
    if (row.state === "unassigned") return "No officer assigned yet";
    if (row.state === "noShow") return "Never clocked in";
    if (row.state === "missedClockIn") return `Due on at ${dayjs(row.start).format("HH:mm")} - not clocked in`;
    if (!row.clockIn) return row.state === "awaiting" ? `Due on at ${dayjs(row.start).format("HH:mm")}` : "";
    const parts = [`${row.source === "mobileService" ? "Started" : "Clocked in"} ${dayjs(row.clockIn.at).format("HH:mm")}${row.clockIn.late ? " (late)" : ""}${row.clockIn.booked ? " (booked)" : ""}`];
    if (row.clockIn.gps) parts.push("GPS ✓");
    if (row.clockIn.selfie) parts.push("selfie ✓");
    if (row.clockOut) parts.push(`${row.source === "mobileService" ? "finished" : "off"} ${dayjs(row.clockOut.at).format("HH:mm")}`);
    if (row.checkCalls?.due) parts.push(`check calls ${Math.min(row.checkCalls.done, row.checkCalls.due)}/${row.checkCalls.due}`);
    return parts.join(" · ");
};

export const Dashboard = () => {
    const navigate = useNavigate();
    const [customerDetail, setCustomerDetail] = useState(null);
    const [requestModalOpen, setRequestModalOpen] = useState(false);
    const [filter, setFilter] = useState("all");
    const [openDuty, setOpenDuty] = useState(null);
    // a customer logo URL that doesn't load falls back to initials, not an empty box
    const [logoFailed, setLogoFailed] = useState(false);

    useEffect(() => {
        const detail = localStorage.getItem("customerDetail");
        if (detail) setCustomerDetail(JSON.parse(detail));
    }, []);

    // the browser's own day, so "today" is the customer's today
    const [day, setDay] = useState(() => dayjs().format("YYYY-MM-DD"));
    const todayVars = useMemo(() => ({
        from: dayjs(day).startOf("day").toISOString(),
        to: dayjs(day).endOf("day").toISOString(),
    }), [day]);

    const { data: todayData, loading: todayLoading, refetch: refetchToday } = useQuery(getCustomerPortalTodayQuery, {
        variables: todayVars,
        fetchPolicy: "cache-and-network",
        pollInterval: 60000,
        notifyOnNetworkStatusChange: false,
    });

    // back on the tab -> fresh data (and roll over to the new day after midnight)
    useEffect(() => {
        const onFocus = () => {
            const now = dayjs().format("YYYY-MM-DD");
            if (now !== day) setDay(now);
            else refetchToday();
        };
        window.addEventListener("focus", onFocus);
        return () => window.removeEventListener("focus", onFocus);
    }, [day, refetchToday]);

    const { data: overviewData } = useQuery(getCustomerPortalOverviewQuery, {
        variables: { startDate: dayjs().format("YYYY-MM-DD"), endDate: dayjs().format("YYYY-MM-DD") },
        fetchPolicy: "cache-and-network",
    });

    const { data: sitesData } = useQuery(getAllSitesForCustomerQuery, {
        variables: { customerId: customerDetail?._id },
        skip: !customerDetail?._id,
        fetchPolicy: "cache-and-network",
    });

    const { data: requestsData, loading: requestsLoading, refetch: refetchRequests } = useQuery(getCustomerDutyRequestsQuery, {
        fetchPolicy: "cache-and-network",
    });

    const { data: alertsData, loading: alertsLoading } = useQuery(getCustomerIncidentAlertsQuery, {
        fetchPolicy: "cache-and-network",
    });

    const today = todayData?.getCustomerPortalToday?.response;
    const rows = useMemo(() => (today?.rows || [])
        .map((r) => ({ ...r, group: groupOf(r.state) }))
        .sort((a, b) => (GROUP_ORDER[a.group] - GROUP_ORDER[b.group]) || (dayjs(a.start).valueOf() - dayjs(b.start).valueOf())),
    [today]);
    const counts = today?.counts || {};
    const visibleRows = filter === "all" ? rows : rows.filter((r) => r.group === filter);
    const loadingToday = todayLoading && !today;

    const totals = overviewData?.getCustomerPortalOverview?.response?.totals || {};
    const sites = sitesData?.getAllSiteForCustomer?.response || [];
    const siteCount = totals.sites ?? sites.length;

    const requests = [...(requestsData?.getCustomerDutyRequests?.response || [])]
        .sort((a, b) => dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf());
    const pendingRequests = requests.filter((r) => r.status === "pending").length;
    const recentIncidents = (alertsData?.getCustomerIncidentAlerts?.response?.recent || []).slice(0, 5);

    const activeGroup = GROUPS.find((g) => g.key === filter);

    return (
        <div className="cdb-page">
            <Navbar customerDetail={customerDetail} />

            <div className="cdb-body">

                {/* ── Header ── */}
                <header className="cdb-header">
                    <div className="cdb-identity">
                        {customerDetail?.metadata?.logolink && !logoFailed ? (
                            <div className="cdb-logo">
                                <img src={customerDetail.metadata.logolink} alt="" onError={() => setLogoFailed(true)} />
                            </div>
                        ) : (
                            <div className="cdb-avatar">{getInitials(customerDetail?.customername)}</div>
                        )}
                        <div>
                            <div className="cdb-eyebrow">{getGreeting()} · {dayjs().format("dddd D MMMM YYYY")}</div>
                            <h1 className="cdb-title">{customerDetail?.customername || "Welcome"}</h1>
                            <div className="cdb-meta">
                                <span><b>{siteCount}</b> active site{siteCount === 1 ? "" : "s"}</span>
                                <span><b>{counts.onDuty ?? 0}</b> officer{counts.onDuty === 1 ? "" : "s"} on duty now</span>
                                {pendingRequests > 0 && <span><b>{pendingRequests}</b> request{pendingRequests === 1 ? "" : "s"} awaiting approval</span>}
                            </div>
                        </div>
                    </div>

                    <div className="cdb-header-actions">
                        <button type="button" className="cdb-btn" onClick={() => navigate("/customer/schedule")}>View schedule</button>
                        <button type="button" className="cdb-btn cdb-btn-primary" onClick={() => setRequestModalOpen(true)}>
                            Request a duty
                        </button>
                    </div>
                </header>

                {/* ── Today at a glance ── */}
                <div className="cdb-kpis cp-kpis-today">
                    {GROUPS.map((g) => {
                        const value = counts[g.key] ?? 0;
                        const alert = g.key === "attention" && value > 0;
                        return (
                            <button
                                key={g.key}
                                type="button"
                                className={`cdb-kpi cdb-kpi-clickable cp-tile${filter === g.key ? " cp-tile-active" : ""}${alert ? " cp-tile-alert" : ""}`}
                                onClick={() => setFilter(filter === g.key ? "all" : g.key)}
                                aria-pressed={filter === g.key}
                            >
                                <span className="cdb-kpi-label">{g.label}</span>
                                {loadingToday ? <Skeleton.Input active size="small" /> : <span className="cdb-kpi-value">{value}</span>}
                                <span className="cdb-kpi-sub">{filter === g.key ? "Showing below · tap to show all" : g.sub}</span>
                            </button>
                        );
                    })}
                    <div className="cdb-kpi">
                        <span className="cdb-kpi-label">Keys held</span>
                        {overviewData ? <span className="cdb-kpi-value">{totals.keySets ?? 0}</span> : <Skeleton.Input active size="small" />}
                        <span className="cdb-kpi-sub">
                            {totals.keySets ? `${totals.physicalKeys} physical key${totals.physicalKeys === 1 ? "" : "s"} in our custody` : "No keys held for your sites"}
                        </span>
                    </div>
                </div>

                {/* ── Today's duties ── */}
                <Card
                    title={activeGroup ? `Today · ${activeGroup.label}` : "Today's duties"}
                    action={
                        <div className="cp-card-tools">
                            {today?.now && <span className="cp-updated">Updated {dayjs(today.now).format("HH:mm")}</span>}
                            <button type="button" className="cdb-link" onClick={() => refetchToday()}>Refresh</button>
                        </div>
                    }
                >
                    {loadingToday ? <Skeleton active paragraph={{ rows: 4 }} /> : visibleRows.length === 0 ? (
                        <div className="cdb-empty">
                            {activeGroup ? activeGroup.empty : (
                                <>No duties at your sites today. <button type="button" className="cdb-link" onClick={() => setRequestModalOpen(true)}>Request a duty</button></>
                            )}
                        </div>
                    ) : (
                        <ul className="cdb-list">
                            {visibleRows.map((r) => {
                                const m = stateMeta(r.state);
                                return (
                                    <li key={r._id} className="cdb-list-item cdb-clickable cp-duty" onClick={() => setOpenDuty(r)}>
                                        <span className="cp-duty-bar" style={{ background: m.color }} />
                                        <div className="cp-duty-time">
                                            <b>{dayjs(r.start).format("HH:mm")}</b>
                                            <span>{formatHours(r.hours)}</span>
                                        </div>
                                        <div className="cdb-list-main">
                                            <div className="cdb-list-title">{r.siteName} <span className="cdb-muted">· {officerName(r)}</span></div>
                                            <div className="cdb-list-sub">{timeRange(r.start, r.end)} · {r.dutyType}{proofLine(r) ? ` · ${proofLine(r)}` : ""}</div>
                                        </div>
                                        <Pill label={m.label} color={m.color} bg={m.bg} />
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </Card>

                {/* ── Latest ── */}
                <div className="cdb-row cdb-row-halves">
                    <Card
                        title="Duty requests"
                        action={<button type="button" className="cdb-link" onClick={() => navigate("/customer/duty-requests")}>View all</button>}
                    >
                        {requestsLoading && !requestsData ? <Skeleton active paragraph={{ rows: 3 }} /> : requests.length === 0 ? (
                            <div className="cdb-empty">
                                No requests yet.{" "}
                                <button type="button" className="cdb-link" onClick={() => setRequestModalOpen(true)}>Request a duty</button>
                            </div>
                        ) : (
                            <ul className="cdb-list">
                                {requests.slice(0, 5).map((r) => {
                                    const s = REQUEST_STATUS[r.status] || REQUEST_STATUS.pending;
                                    const date = r.preferredDate || (r.preferredStartTime ? r.preferredStartTime.split(" ")[0] : null);
                                    return (
                                        <li key={r._id} className="cdb-list-item">
                                            <div className="cdb-list-main">
                                                <div className="cdb-list-title">
                                                    {r.serviceType === "guarding" ? "Guarding" : "Mobile response"} <span className="cdb-muted">at</span> {r.siteDetail?.sitename || "—"}
                                                </div>
                                                <div className="cdb-list-sub">{date ? `For ${date} · ` : ""}requested {dayjs(r.createdAt).fromNow()}</div>
                                            </div>
                                            <Pill label={s.label} color={s.color} />
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </Card>

                    <Card
                        title="Recent incidents"
                        action={<button type="button" className="cdb-link" onClick={() => navigate("/customer/incidents")}>View all</button>}
                    >
                        {alertsLoading && !alertsData ? <Skeleton active paragraph={{ rows: 3 }} /> : recentIncidents.length === 0 ? (
                            <div className="cdb-empty">No incidents have been reported on your sites.</div>
                        ) : (
                            <ul className="cdb-list">
                                {recentIncidents.map((i) => {
                                    const s = getStatusMeta(i.status);
                                    return (
                                        <li key={i._id} className="cdb-list-item cdb-clickable" onClick={() => navigate(`/customer/incidents/${i._id}`)}>
                                            <div className="cdb-list-main">
                                                <div className="cdb-list-title">{i.incidentType || "Incident"} <span className="cdb-muted">at</span> {i.site}</div>
                                                <div className="cdb-list-sub">Reported {dayjs(i.reportedAt).fromNow()}</div>
                                            </div>
                                            <Pill label={s.label} color={s.color} />
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </Card>
                </div>
            </div>

            <DutyDrawer duty={openDuty} onClose={() => setOpenDuty(null)} />

            {requestModalOpen && (
                <DutyRequestFormModal
                    visible={requestModalOpen}
                    onClose={() => setRequestModalOpen(false)}
                    sites={sites}
                    onSuccess={() => refetchRequests()}
                />
            )}
        </div>
    );
};
